<!-- eslint-disable id-length -->
<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script setup lang="ts">
    import { VENUE_HOST_EVENT, VENUE_HOST_RESET } from '@/constants/events';
    import { buildMessage, FRAME_TO_HOST, isFramedContext } from '@/frame/bridge';
    import { openReservationProvider } from '@/helpers/reservation-openers';
    import { getContrastColor } from '@/helpers/text';
    import type { EventBusType } from '@/plugins/event-bus-plugin';
    import type { EventTypeTyping, WidgetPartTyping } from '@/typings/widget';
    import { savePendingMessage } from '@/utils/pending-message-storage';
    import Icon from '@shared/components/ui/icon/icon.vue';
    import { useSafeTimeout } from '@shared/composables/useTimeout';
    import type { Ref } from 'vue';

    const props = defineProps<{
        venueEventTypes: EventTypeTyping[]
        brandColor?: string
    }>();

    const emit = defineEmits<{(event: 'goToPath' | 'goToPackages' | 'goToContact' | 'goToAppointment' | 'goToChatbox')}>();

    const { t } = useI18n();

    // Stores
    const { venueWidgetParts, isPackageActive, isDigitalHostActive, openWindow } = storeToRefs(useWidgetVenueStore());

    // Constants
    const EXIT_DELAY = 3000;
    const NEXT_PART = 0.5;
    const VENUE_HOST_DELAY = 100;
    const NOT_ALLOWED_IDS = [
        1, 2, 7,
    ];

    // Composables
    const eventBus = inject<EventBusType>('eventBus');
    const safeTimeout = useSafeTimeout();

    // Refs
    const dynamicIndex: Ref<number> = ref(0);
    const intervalId:Ref<Timer> = ref({} as Timer);
    const invisible: Ref<boolean> = ref(false);
    const messageInput: Ref<string> = ref('');

    const getPartSettingName = (widgetPart: WidgetPartTyping): string => widgetPart.widgetPartName ?? widgetPart.name ?? '';

    // Computed
    const eventTypeChanger: ComputedRef<string[]> = computed(() => props.venueEventTypes.map(eventType => eventType.name));
    const chatBoxPart: ComputedRef<WidgetPartTyping | undefined> = computed(() => venueWidgetParts.value.find(part => part.widgetPartId === 7));
    const shouldBeVisible: ComputedRef<(widgetPart: WidgetPartTyping) => boolean> = computed(() => (widgetPart: WidgetPartTyping) => {
        if (NOT_ALLOWED_IDS.includes(Number(widgetPart.widgetPartId))) return false;
        if (widgetPart.widgetPartId === 3 && (widgetPart.activated || isDigitalHostActive.value)) return true;
        if (getPartSettingName(widgetPart)
            .startsWith('chat-')) return false;
        return widgetPart.activated;
    });

    /**
     * Handle chatbox form submission
     * Saves message if non-empty and navigates to chat
     */
    const handleChatSubmit = (): void => {
        const trimmedMessage = messageInput.value.trim();

        // Save to localStorage if message is not empty
        if (trimmedMessage) {
            savePendingMessage(trimmedMessage);
        }

        // Navigate to chatbox (even if empty)
        emit('goToChatbox');

        // Clear input after submission
        messageInput.value = '';
    };

    const goToPath = (): void => {
        invisible.value = true;
        eventBus?.emit(VENUE_HOST_EVENT, { step: NEXT_PART });
        safeTimeout.setSafeTimeout(() => {
            emit('goToPath');
        }, EXIT_DELAY);
    };

    const handleOpenReservations = (widgetPart: WidgetPartTyping): void => {
        // Plain URL: opens fine from inside the iframe (user-gesture popup).
        if (widgetPart.partnerId === 5) {
            window.open(String(widgetPart.value), '_blank');
            return;
        }

        const partnerId = Number(widgetPart.partnerId);

        // SDK-based providers live on the host page. Framed -> forward to the host shell;
        // dev inline -> run directly (same page context). See reservation-openers.ts.
        if (isFramedContext()) {
            window.parent?.postMessage(buildMessage(FRAME_TO_HOST.OPEN_RESERVATION, { partnerId }), '*');
        } else {
            openReservationProvider(partnerId);
        }
        openWindow.value = false;
    };

    const handleWidgetPartRouting = (widgetPart: WidgetPartTyping): void => {
        switch (widgetPart.widgetPartId) {
        case 3:
            goToPath();
            break;

        case 4:
            emit('goToPackages');
            break;

        case 5:
            emit('goToContact');
            break;

        case 6:
            emit('goToAppointment');
            break;

        case 8:
        case 9:
        case 10:
            handleOpenReservations(widgetPart);
            break;

        default:
            window.open(String(widgetPart.value), '_blank');
            break;
        }
    };

    // Function to loop through event types over time
    const loopIndexOverTime = (): void => {
        const timeInSeconds = 2; // Total time for the animation cycle
        const intervalDuration = timeInSeconds * 1000; // Convert to milliseconds
        dynamicIndex.value = 0;

        intervalId.value = setInterval(() => {
            dynamicIndex.value = (dynamicIndex.value + 1) % eventTypeChanger.value.length;
        }, intervalDuration);
    };

    // Clean up interval on unmount
    onBeforeUnmount(() => {
        clearInterval(intervalId.value);
    });

    onMounted(() => {
        eventBus?.emit(VENUE_HOST_RESET);
        setTimeout(() => {
            eventBus?.emit(VENUE_HOST_EVENT, { step: 0 });
        }, VENUE_HOST_DELAY);
    });

    // Watch for changes to venueEventTypes and restart the animation
    watch(() => props.venueEventTypes, () => {
        clearInterval(intervalId.value);
        loopIndexOverTime();
    });
</script>

<template>
    <div
        :style="{
            '--hover-text-color': getContrastColor(brandColor ?? '#282829'),
            '--hover-color': brandColor
        }"
        class="welcome-screen"
    >
        <!-- Venue Host Wrapper Target -->
        <div
            id="welcome-host-target"
            class="welcome-screen__host-target"
            :class="{'welcome-screen__host-target--invisible': invisible}"
        />

        <!-- Venue Host Spacer -->
        <div class="welcome-screen__host-spacer" />

        <!-- Hero Menu -->
        <div class="welcome-screen__content">
            <div
                :class="{'welcome-screen--invisible': invisible}"
                class="welcome-screen__menu"
            >
                <template
                    v-for="widgetPart in venueWidgetParts"
                    :key="widgetPart.id"
                >
                    <button
                        v-if="shouldBeVisible(widgetPart)"
                        type="button"
                        class="welcome-screen__menu__item"
                        @click="handleWidgetPartRouting(widgetPart)"
                    >
                        <span>
                            {{ getPartSettingName(widgetPart) }}
                        </span>
                        <Icon
                            name="chevronRight"
                            size="medium"
                        />
                    </button>
                </template>
            </div>
            <div
                v-if="chatBoxPart?.activated"
                class="welcome-screen__chatbox"
            >
                <form
                    class="welcome-screen__chatbox-form"
                    @submit.prevent="handleChatSubmit"
                >
                    <div class="welcome-screen__chatbox-control">
                        <input
                            v-model="messageInput"
                            type="text"
                            class="welcome-screen__chatbox-input"
                            :placeholder="t('widget.buttons.chatbox')"
                            maxlength="500"
                        >
                        <button
                            type="submit"
                            class="btn btn--white btn--normal btn--square"
                        >
                            <span>
                                <Icon
                                    name="arrowRight"
                                    size="medium"
                                />
                            </span>
                        </button>
                    </div>
                </form>
                <label>
                    {{ t('widget.messages.chatbox') }}
                </label>
            </div>
        </div>

        <div
            v-if="isPackageActive"
            class="welcome-screen__see"
            :class="{'welcome-screen--invisible': invisible}"
        >
            <div
                class="welcome-screen__see__content"
                @click="emit('goToPackages')"
            >
                {{ t('widget.welcome.see') }}
                <Icon
                    name="chevronBottom"
                    size="medium"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.welcome-screen {
    min-block-size: 500px;
    padding-block: var(--spacing-04) 0;
    transform: scale(1);

    @include min-width(md) {
        padding-block: 0;
    }

    &--invisible {
        opacity: 0;
        pointer-events: none;
    }

    &__content {
        display: flex;
        flex-direction: column-reverse;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-04);

        @include min-width(md) {
            flex-direction: row;
            gap: var(--spacing-10);
            padding-inline: var(--spacing-05);
        }

        @include min-width(lg) {
            gap: calc(var(--spacing-20) + var(--spacing-07));
            padding-inline: var(--spacing-07);
        }

        > * {
            flex: 1;
        }
    }

    &__menu,
    &__chatbox {
        display: flex;
        flex-direction: column;
        align-items: center;
        inline-size: 100%;
        padding-inline: var(--spacing-03);

        @include min-width(md) {
            padding-inline: 0
        }
    }

    &__menu {
        justify-content: flex-start;
        gap: var(--spacing-02);

        @include min-width(md) {
            justify-content: center;
            margin-block: var(--spacing-04);
            max-inline-size: 50%;
            min-block-size: 500px;
            overflow-y: auto;
        }

        @include min-width(lg) {
            margin-block: var(--spacing-06);
        }

        &__item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border: 1px solid var(--color-white);
            border-radius: var(--border-radius-sm);
            background: var(--background-secondary-highlight);
            block-size: var(--size-10);
            inline-size: 100%;
            padding-block: var(--spacing-03);
            padding-inline: var(--spacing-04);

            @include min-width(lg) {
                block-size: var(--size-11);
            }

            span {
                font-size: var(--font-regular);
                font-weight: var(--font-weight-medium);
            }

            &:hover {
                background-color: var(--hover-color);
                color: var(--hover-text-color);

                span {
                    color: var(--hover-text-color);
                }
            }
        }
    }

    &__chatbox {
        justify-content: flex-end;
        max-inline-size: 372px; // max width of the digital host

        @include min-width(md) {
            max-inline-size: 272px; // max width of the digital host
            min-block-size: 500px; // same height as venue host container
        }

        @include min-width(lg) {
            max-inline-size: 372px; // max width of the digital host
            min-block-size: 550px; // same height as venue host container
        }

        &-form {
            inline-size: 100%;
        }

        &-control {
            display: flex;
            align-items: center;
            border: 1px solid var(--border-tertiary);
            border-radius: var(--border-radius-sm);
            background: var(--background-secondary-highlight);
            block-size: var(--size-10);
        }

        &-input {
            color: var(--text-primary);
            font-size: var(--font-regular);
            inline-size: 100%;
            padding-block: var(--spacing-03);
            padding-inline: var(--spacing-04);
            transition: border-color 0.2s ease;

            &::placeholder {
                color: var(--text-placeholder);
            }

            &:focus {
                border-color: var(--hover-color);
                outline: none;
            }
        }

        .btn {
            display: inline-flex;
            padding: 0;
            border: none;
            margin: 0;
            background-color: transparent;
            margin-inline-end: var(--spacing-03);

            span {
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-rounded);
                background-color: var(--background-secondary);
                padding-block: var(--spacing-01);
                padding-inline: var(--spacing-02);
            }
        }

        label {
            color: var(--text-secondary);
            font-size: var(--font-small);
            margin-block-start: var(--spacing-03);
        }
    }

    &__see {
        display: flex;
        justify-content: center;
        margin-block-start: var(--spacing-05);

        @include min-width(lg) {
            margin-block-start: var(--spacing-02);
        }

        &::before,
        &::after {
            display: block;
            background: var(--border-tertiary);
            block-size: 1px;
            content: '';
            inline-size: 100%;
            margin-block-start: var(--spacing-02);
        }

        &__content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: var(--font-regular);
            font-weight: var(--font-weight-medium);
            padding-inline: var(--spacing-04);
            white-space: nowrap;
        }
    }

    &__host-target {
        position: absolute;
        overflow: hidden;
        block-size: 100%;
        inline-size: 100%;
        inset-block: 0 0;
        inset-inline: 0 0;
        pointer-events: none;

        &--invisible {
            position: fixed;
            overflow: hidden;
        }
    }

    &__host-spacer {
        position: relative;
        block-size: 380px;
        padding-block: 72% 10%;
        padding-block-end: 15%;

        @include min-width(md) {
            position: absolute;
            z-index: -1;
            margin: 0;
            block-size: calc(100% - 125px);
            inline-size: 100%;
            inset-block-start: 0;
            padding-block-start: 0%;
        }
    }
}
</style>
